<template>
    <div
        ref="wrapper"
        v-on-clickaway="handleClickaway"
    >

        <b-tooltip
            v-show="tooltip"
            ref="tooltip"
            :target="id"
            placement="top"
        >
            {{ tooltip }}
        </b-tooltip>

        <div :id="`popover-container-${id}`" />
        <b-popover
            :show="isPopoverOpen"
            :target="id"
            placement="top"
            triggers=""
            boundary-padding="10"
            :container="`#popover-container-${id}`"
            :custom-class="large ? 'popover-lg' : ''"
            @shown="emitEditingEvent"
        >
            <slot
                name="input"
                @save="save"
            />

            <slot name="help">
                <p class="small text-muted my-1">{{ help }}</p>
            </slot>

            <div class="d-flex justify-content-between mt-2">
                <b-button
                    variant="danger"
                    size="sm"
                    :disabled="isSaving"
                    @click="stopEditing"
                >
                    <i class="fa fa-close mr-1" />
                    <span class="mr-1">Cancel</span>
                </b-button>
                <b-button
                    class="d-flex align-items-center ml-2"
                    variant="success"
                    size="sm"
                    :disabled="isSaving"
                    @click="save"
                >
                    <span class="mr-1">Save</span>
                    <Spinner
                        v-show="isSaving"
                        variant="success"
                        size="tiny"
                    />
                    <i
                        v-show="!isSaving"
                        class="fa fa-check"
                    />
                </b-button>
            </div>
        </b-popover>

        <div
            :id="id"
            class="d-inline-flex underline-dotted"
            @click="startEditing"
        >
            <slot name="preview" />
        </div>

    </div>
</template>

<script>
import { vueUidMixin } from 'vue-uid';
import { mixin as clickaway } from 'vue-clickaway';

export default {

    mixins: [
        vueUidMixin,
        clickaway,
    ],

    props: {
        tooltip: {
            type: String,
            default: '',
        },
        closeOnClickaway: {
            type: Boolean,
            default: false,
        },
        saveTrigger: {
            type: Boolean,
            default: false,
        },
        help: {
            type: String,
            default: '',
        },
        large: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isPopoverOpen: false,
            isSaving: false,
            editableUid: null,
        };
    },

    computed: {
        id() {
            return this.$_uid.toString();
        },
    },

    watch: {
        /**
         * Whenever this component receives a new saveTrigger value,
         * we call the save method. This makes it easy to trigger a save from a component
         * which is inside of this components "input" slot.
         */
        saveTrigger(oldVal, newVal) {
            oldVal !== newVal && this.save();
        },
    },

    async created() {
        // we get a unique and predicatably incremented uid from the store
        // which allows us to tab to the next and previos editable components
        this.editableUid = await this.$store.dispatch('getEditableUid');
        // we register the listener that will be used to open this component from another editable
        this.$eventBus.$on(`open-editable-${this.editableUid}`, this.startEditing);
    },

    beforeDestroy() {
        this.removeShortkeyEventListener();
    },

    methods: {
        startEditing() {
            this.disableTooltip();
            this.addShortkeyEventListener();
            this.isPopoverOpen = true;
        },
        emitEditingEvent() {
            this.$emit('start-editing', this.editableUid);
        },
        stopEditing() {
            this.isPopoverOpen = false;
            this.removeShortkeyEventListener();
            this.enableTooltip();
        },
        addShortkeyEventListener() {
            this.$refs.wrapper.addEventListener('keydown', this.shortkeyHandler);
        },
        removeShortkeyEventListener() {
            this.$refs.wrapper.removeEventListener('keydown', this.shortkeyHandler);
        },
        shortkeyHandler(e) {
            if (e.keyCode === 9) {
                e.preventDefault();
                e.stopPropagation();
                this.save();
                const editableUidToOpen = e.getModifierState('Shift') ? this.editableUid - 1 : this.editableUid + 1;
                this.$eventBus.$emit(`open-editable-${editableUidToOpen}`);
                this.removeShortkeyEventListener();
            } else if (e.keyCode === 27) {
                e.preventDefault();
                e.stopPropagation();
                this.stopEditing();
            }
        },
        handleClickaway() {
            this.closeOnClickaway && this.isPopoverOpen && this.stopEditing();
        },
        save() {
            this.isSaving = true;
            this.$emit('save', {
                done: () => {
                    this.isSaving = false;
                    this.stopEditing();
                },
            });
        },
        disableTooltip() {
            this.$refs.tooltip.$emit('close');
            this.$refs.tooltip.$emit('disable');
        },
        enableTooltip() {
            this.$refs.tooltip.$emit('enable');
        },
    },

};
</script>

<style lang="scss" scoped>
.underline-dotted {
    text-decoration: underline;
    text-decoration-style: dotted;
    cursor: pointer;
}
</style>
